import { action, makeObservable, observable, runInAction } from 'mobx';
import { productService, publicCatalogService, getClinicToken } from '../../services';
import {
  ApiError,
  ClinicProduct,
  ClinicProductQuery,
} from '../../types';

const pickService = () =>
  getClinicToken()
    ? {
        list: productService.list,
        detail: productService.detail,
      }
    : {
        list: publicCatalogService.list,
        detail: publicCatalogService.detail,
      };

const DEFAULT_TAKE = 12;

export class CatalogStore {
  items: ClinicProduct[] = [];
  total = 0;
  query: ClinicProductQuery = { skip: 0, take: DEFAULT_TAKE };
  loadingList = false;
  listError: ApiError | null = null;

  detail: ClinicProduct | null = null;
  loadingDetail = false;
  detailError: ApiError | null = null;

  constructor() {
    makeObservable(this, {
      items: observable,
      total: observable,
      query: observable,
      loadingList: observable,
      listError: observable,
      detail: observable,
      loadingDetail: observable,
      detailError: observable,
      setQuery: action,
      resetQuery: action,
      loadList: action,
      loadDetail: action,
      clearDetail: action,
    });
  }

  setQuery(patch: Partial<ClinicProductQuery>): void {
    const next: ClinicProductQuery = { ...this.query, ...patch };
    if (!('skip' in patch)) next.skip = 0;
    this.query = next;
  }

  resetQuery(): void {
    this.query = { skip: 0, take: DEFAULT_TAKE };
  }

  async loadList(): Promise<void> {
    this.loadingList = true;
    this.listError = null;
    try {
      const res = await pickService().list(this.query);
      runInAction(() => {
        this.items = res.items;
        this.total = res.total;
      });
    } catch (err) {
      runInAction(() => {
        this.listError = err as ApiError;
      });
    } finally {
      runInAction(() => {
        this.loadingList = false;
      });
    }
  }

  async loadDetail(productId: string): Promise<void> {
    this.loadingDetail = true;
    this.detailError = null;
    this.detail = null;
    try {
      const product = await pickService().detail(productId);
      runInAction(() => {
        this.detail = product;
      });
    } catch (err) {
      runInAction(() => {
        this.detailError = err as ApiError;
      });
    } finally {
      runInAction(() => {
        this.loadingDetail = false;
      });
    }
  }

  clearDetail(): void {
    this.detail = null;
    this.detailError = null;
  }
}
